<template>
    <div class="h-100">
        <ob1-cards :is-multiple="true" :is-inversed="isBlack" :with-composite-link="false">
            <img
                v-if="offer.technologyLogo"
                :src="offer.technologyLogo"
                alt="logo 5G Orange"
                class="logo-5g"
                aria-hidden="true"
            >
            <span
                v-if="showBanner(offer.banner)"
                v-msh-html="offer.banner"
                class="ob1-tag ob1-tag-primary card-tag fw-bold"
            />
            <div class="tile-content d-flex flex-column pt-2">
                <div>
                    <div class="title-wrapper px-1 px-xl-2 d-flex flex-column">
                        <h2 class="visually-hidden">
                            {{ nameAccessibility }}
                        </h2>
                        <p class="pt-xl-1 py-0 mb-0 fw-bold" aria-hidden="true">
                            {{ getTitle(offer.offerType) }}
                        </p>
                        <h2 class="h1 py-0 mb-0" aria-hidden="true">
                            {{ offer.secondaryShortLabel }}
                        </h2>
                    </div>
                </div>

                <div>
                    <div class="price-wrapper" :class="isShowOpenDiscountMessage ? 'my-1 mx-1 mx-xl-2 ps-1 bg-greige' : 'mb-0 px-1 px-xl-2'">
                        <template v-if="offer.openDiscountMessage">
                            <div v-msh-html="offer.openDiscountMessage" class="fw-bold small text-dark pt-1 pb-0 pe-1" />
                        </template>
                        <Price
                            :price="offer.price.price"
                            :initial-price="offer.price.initialPrice"
                            :price-details="offer.price.priceDetails ? offer.price.priceDetails : undefined"
                            :show-from="offer.price.showFrom"
                            :show-by-month="offer.price.showByMonth"
                            :show-promo-duration="offer.price.showPromoDuration"
                            :show-promo-duration-first-months="offer.price.showPromoDurationFirstMonths"
                            :show-children-promotion-price="offer.price.showChildrenPromotionPrice"
                            :duration="offer.price.duration"
                            :is-cents-format="false"
                            :is-compact="true"
                            :is-inverse="isBlack && !isShowOpenDiscountMessage"
                        />
                    </div>
                </div>

                <div>
                    <div class="claim-wrapper">
                        <div v-if="offer.claims" class="mx-1 mx-xl-2">
                            <ul class="row list-unstyled" aria-label="Avantages du forfait">
                                <li v-for="(claim, index) in offer.claims" :key="index">
                                    <offer-claim :key="index" :claim="claim" :is-black="isBlack" />
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div>
                    <div class="advantage-message-wrapper">
                        <div v-if="advantageOfferMobileMessage" class="d-flex flex-row align-items-center px-1 px-xl-2">
                            <p class="mb-0 pt-0">
                                <span class="icon-Mobile-payment font-size-26" aria-hidden="true" />
                            </p>
                            <p class="mb-0 pt-0">
                                <span v-msh-html="advantageOfferMobileMessage" class="fw-bold" />
                            </p>
                        </div>
                    </div>
                </div>

                <div class="d-flex justify-content-center px-1 px-xl-2">
                    <offer-cta-with-device-popin
                        v-if="isAddOfferWithPopin"
                        class="mx-0 px-0 w-100"
                        :is-wide="true"
                        :is-primary="true"
                        :is-inverse="isBlack"
                        :from-cds="true"
                        :is-sub="isSub"
                        :label="ctaLabel"
                        :offer-name="offer.name"
                        :popin-id="'addOfferWithDevicePopIn-' + offer.idSeo"
                        :popin-ctas-dto="offer.addOfferDto.popinWithDeviceCdsDto"
                        :tracking-category="ctaTrackingCategory"
                        :tracking-label="getLabelToTmsFormat(offer.name)"
                    />
                    <ob1-boutons
                        v-else-if="offer.addOfferDto"
                        :is-button="false"
                        :is-primary="true"
                        :is-inverse="isBlack"
                        :is-wide="true"
                        :label="ctaLabel"
                        :aria-label="'Choisir ce ' + nameAccessibility"
                        :href="offer.addOfferDto.link"
                        :data-oevent-category="ctaTrackingCategory"
                        :data-oevent-action="getLabelToTmsFormat(ctaLabel)"
                        :data-oevent-label="getLabelToTmsFormat(offer.name)"
                    />
                    <offer-cta-with-device-popin
                        v-if="offer.addOfferWithAuthenticationDto"
                        class="mx-0 px-0 w-100"
                        :is-wide="true"
                        :is-primary="true"
                        :is-inverse="isBlack"
                        :from-cds="true"
                        :is-sub="isSub"
                        :label="ctaLabel"
                        :offer-name="offer.name + ' ' + offer.offerType"
                        :popin-id="'addOfferWithAuthenticationAndDevicePopIn-' + offer.idSeo"
                        :popin-ctas-dto="offer.addOfferWithAuthenticationDto.popinWithDeviceCdsDto"
                        :tracking-category="ctaTrackingCategory"
                        :tracking-label="getLabelToTmsFormat(offer.name)"
                    />
                    <ob1-boutons
                        v-if="offer.addOfferInCartDto"
                        class="mx-0 px-0 w-100"
                        :is-button="true"
                        :is-primary="true"
                        :is-inverse="isBlack"
                        :label="offer.addOfferInCartDto.label"
                        :aria-label="'Choisir ce ' + nameAccessibility"
                        data-oevent-category="tuile_forfait_vsim"
                        data-oevent-action="ajouter_au_panier"
                        :data-oevent-label="offer.name"
                        @ob1-boutons-click.prevent="onAddOfferInCart()"
                    />
                </div>

                <div class="d-flex justify-content-center pb-1 px-1 px-xl-2">
                    <ob1-liens
                        is-button
                        :with-arrow="true"
                        :is-inverse="isBlack"
                        :aria-label="openOffersPopinAccessibility"
                        data-bs-toggle="modal"
                        :data-test-id="'btn-popin-detail-' + offer.idSeo"
                        modal-id="offer-details-popin"
                        :text="linkCdsDetailPageOrConfigureLabel"
                        :data-oevent-category="ctaTrackingCategory"
                        :data-oevent-action="getLabelToTmsFormat(offer.linkCdsDetailPageLabel)"
                        :data-oevent-label="getLabelToTmsFormat(offer.name)"
                        @ob1-liens-click="openOffersPopin(offer)"
                    />
                </div>
            </div>
        </ob1-cards>
    </div>
</template>

<script>
import OfferCtaWithDevicePopin from "../../../common/offerdetails/OfferCtaWithDevicePopin.vue";
import Price from "../../../../common/components/ob1/Price.vue";
import OfferClaim from "../../../common/vitrine/OfferClaim.vue";


export default {
    components: {
        OfferClaim,
        Price,
        OfferCtaWithDevicePopin,
    },
    props: {
        offer: {
            type: Object,
            required: true
        },
        sectionId: {
            type: String,
            required: true
        },
        isSub: {
            type: Boolean,
            required: true
        },
        isFmr: {
            type: Boolean,
            required: true
        },
        isBlack: {
            type: Boolean,
            required: true
        },
        advantageOfferMobileMessage: {
            type: String,
            required: false,
            default: null
        },
    },
    computed: {
        openOffersPopinAccessibility() {
            return  this.linkCdsDetailPageOrConfigureLabel + ' du ' + this.nameAccessibility;
        },
        linkCdsDetailPageOrConfigureLabel() {
            if (this.offer.blocked === undefined) {
                return this.offer.linkCdsDetailPageLabel;
            } else {
                return this.offer.linkCdsDetailPageAndConfigureLabel;
            }
        },
        blockedAndUnBlockedLabel() {
            if (this.offer.blocked === undefined) {
                return '';
            } else {
                return this.offer.blocked ? this.offer.miniOfferCdsTilesTypeBlocked : this.offer.miniOfferCdsTilesTypeUnBlocked;
            }
        },
        attributes() {
            return this.offer.attributes
        },
        ctaLabel() {
            return this.isSub ? this.offer.ctaCdsChoiceOfferAndMobileLabel : this.offer.ctaCdsChoiceOfferLabel
        },

        isShowOpenDiscountMessage() {
            return this.offer.openDiscountMessage !== undefined
        },
        ctaTrackingCategory() {
            return this.isSub ? 'tuile_forfait_vsub' : 'tuile_forfait_vsim'
        },
        nameAccessibility() {
            let label5GAccessibility = this.offer.is5G ? '5G' : '';
            let brandAccessibility = this.offer.brand === 'ORANGE' ? 'Forfait Orange' : 'Forfait Sosh';

            let secondaryShortLabelAccessibility = this.offer.secondaryShortLabel
                ? this.offer.secondaryShortLabel
                    .replace(/Mo/g, ' méga Octet')
                    .replace(/Go/g, ' giga Octet')
                : '';

            return brandAccessibility + ' ' + this.offer.type + ' ' + this.offer.offerType + ' ' + secondaryShortLabelAccessibility + ' ' + label5GAccessibility;
        },
        isAddOfferWithPopin() {
            return this.offer.addOfferDto?.popinWithDeviceCdsDto;
        }
    },
    methods: {
        onAddOfferInCart() {
          this.$emit('add-offer-in-cart', this.offer.idSeo)
        },
        showBanner(banner) {
            return banner && banner !== '';
        },
        getTitle(offerType) {
            return offerType ? `${offerType} ${this.blockedAndUnBlockedLabel || ''}`.trim() : '';
        },
        openOffersPopin(offer) {
            Tms.link({category: 'voir_le_detail', action: offer.name, label: 'vers_popin_offres'});
            //Créer un datalayer propre à la popin
            if (window.o_utagview && tmsData) {
                let tmsDataPopin = {...tmsData};
                tmsDataPopin.id_tracking = "jsomEXnY";
                tmsDataPopin.titre_page = "overlay_" + stripAccents(offer.name).split(' ').join('_');
                tmsDataPopin.type_page = "overlay_fiche_forfait";
                tmsDataPopin.offre_prix_ttc = offer.price.initialPrice.toString();
                tmsDataPopin.offre_prix_remise = offer.price.price.toString();
                tmsDataPopin.offre_type = this.isFmr ? "forfait_postpaid_fmo_orange" : "forfait_postpaid_fm_orange";
                tmsDataPopin.offre_libelle = offer.name;
                tmsDataPopin.offre_modele = offer.name;
                tmsDataPopin.offre_qte = 1;
                tmsDataPopin.offre_id = offer.variationCode;
                Tms.view(tmsDataPopin);
            }
            this.$emit('selected-offer-details', offer);
        },
        getLabelToTmsFormat(label) {
            let blockedUnBlockedLabel = '';
            if (this.offer.blocked !== undefined) {
                blockedUnBlockedLabel = this.offer.blocked ? '_bloque' : '_non_bloque';
            }

            return stripAccents(label).split(' ').join('_') + blockedUnBlockedLabel;
        },
    },
}
</script>
<style lang="scss">
.logo-5g {
    position: absolute;
    top: 0;
    right: 0;
    width: 60px;
    object-fit: cover;
    object-position: 14px -14px;
}
.label-5g {
    position: absolute;
    top: 6px;
    right: 6px;
}

//Une classe ajoutée provisoir pour le tag(banner)
//dans une card non pris en compte par ob1
.card-tag {
    position: absolute;
    top: -.0625rem;
    left: -.0625rem;
}

</style>
